import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Building2, User, Loader2, Clock, ArrowLeft, ArrowRight, Shield,
  Package, CheckCircle, MessageCircle, Users, CreditCard, Building,
  Wallet, Copy, Info, Eye, EyeOff, Lock, Zap, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useI18n } from '@/components/I18nProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_META = [
  { icon: Building2, label: 'Firma' },
  { icon: User, label: 'Hesap' },
  { icon: Package, label: 'Paket' },
  { icon: Wallet, label: 'Ödeme' },
];

const features = [
  { icon: MessageCircle, text: 'WhatsApp ile otomatik hatırlatma' },
  { icon: BarChart3, text: 'Canlı dashboard ve raporlama' },
  { icon: Shield, text: 'Güvenli ve KVKK uyumlu' },
  { icon: Zap, text: '7/24 erişim, anlık bildirimler' },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, login } = useAuthStore();
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [packages, setPackages] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [paytrToken, setPaytrToken] = useState(null);
  const [paytrOid, setPaytrOid] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '', tax_number: '', tax_office: '', company_phone: '', company_address: '',
    name: '', email: '', phone: '', password: '', password_confirm: '',
    selected_package_slug: 'trial',
  });

  const [consents, setConsents] = useState({ kvkk: false, privacy: false, membership: false });

  useEffect(() => {
    axios.get(`${API_URL}/api/packages`).then(res => setPackages(res.data)).catch(() => {});
    axios.get(`${API_URL}/api/payment-methods/active`).then(res => setPaymentMethods(res.data)).catch(() => {});
  }, []);

  const set = (key, val) => setFormData((f) => ({ ...f, [key]: val }));
  const selectedPkg = packages.find(p => p.slug === formData.selected_package_slug);
  const isTrial = formData.selected_package_slug === 'trial';
  const yearlyPrice = selectedPkg?.yearly_price || (selectedPkg?.monthly_price || 0) * 12;

  // Steps: Trial = 3 steps (Firma, Hesap, Paket), Paid = 4 steps (Firma, Hesap, Paket, Odeme)
  const totalSteps = isTrial ? 3 : 4;
  const stepsForStepper = isTrial
    ? [STEP_META[0], STEP_META[1], STEP_META[2]]
    : STEP_META;

  const validateStep1 = () => {
    if (!formData.company_name.trim()) { toast.error('Firma adı zorunlu'); return false; }
    if (!formData.tax_number.trim()) { toast.error('Vergi numarası zorunlu'); return false; }
    if (!formData.tax_office.trim()) { toast.error('Vergi dairesi zorunlu'); return false; }
    if (!formData.company_phone.trim()) { toast.error('Firma telefon zorunlu'); return false; }
    if (!formData.company_address.trim()) { toast.error('Adres zorunlu'); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.name.trim()) { toast.error('Ad soyad zorunlu'); return false; }
    if (!formData.email.trim()) { toast.error('E-posta zorunlu'); return false; }
    if (!formData.phone.trim()) { toast.error('Telefon zorunlu'); return false; }
    if (formData.password.length < 8) { toast.error(t('passwordMinLength')); return false; }
    if (!/[A-Z]/.test(formData.password)) { toast.error(t('passwordNeedUpper')); return false; }
    if (!/[a-z]/.test(formData.password)) { toast.error(t('passwordNeedLower')); return false; }
    if (!/[0-9]/.test(formData.password)) { toast.error(t('passwordNeedDigit')); return false; }
    if (formData.password !== formData.password_confirm) { toast.error(t('passwordsNotMatch')); return false; }
    if (!consents.kvkk || !consents.privacy || !consents.membership) { toast.error('Tüm sözleşmeleri onaylamanız gerekmektedir'); return false; }
    return true;
  };

  // TRIAL or BANK TRANSFER: use existing register endpoint
  const handleRegisterDirect = async () => {
    setIsSubmitting(true);
    const result = await register({
      email: formData.email, password: formData.password, name: formData.name, phone: formData.phone,
      role: 'company_admin', company_name: formData.company_name, tax_number: formData.tax_number,
      tax_office: formData.tax_office, company_phone: formData.company_phone, company_address: formData.company_address,
      kvkk_accepted: true, privacy_accepted: true, membership_accepted: true,
      selected_package_slug: formData.selected_package_slug,
      selected_payment_method: selectedPaymentMethod?.id || null,
      selected_payment_method_type: selectedPaymentMethod?.type || null,
    });

    setIsSubmitting(false);
    if (result.success) {
      const pkgName = selectedPkg?.name || 'Ücretsiz Deneme';
      if (isTrial) toast.success(`Kayıt başarılı! ${pkgName} paketi ile 7 günlük deneme süreniz başladı.`);
      else if (selectedPaymentMethod?.type === 'bank_transfer') toast.success(`Kayıt başarılı! ${pkgName} paketi seçildi. Havale yaptıktan sonra aboneliğiniz aktif edilecektir.`);
      else toast.success(`Kayıt başarılı! ${pkgName} paketi ile 7 günlük deneme süreniz başladı.`);
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  // CREDIT CARD: use pre-register endpoint (payment BEFORE account creation)
  const handlePreRegisterPayTR = async () => {
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/paytr/pre-register`, {
        email: formData.email,
        amount: yearlyPrice,
        user_name: formData.name,
        user_phone: formData.phone,
        user_address: formData.company_address || formData.company_name,
        package_name: selectedPkg?.name || 'Abonelik',
        package_slug: formData.selected_package_slug,
        currency: 'TL',
        merchant_ok_url: `${window.location.origin}/payment/success`,
        merchant_fail_url: `${window.location.origin}/payment/fail`,
        registration_data: {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone,
          company_name: formData.company_name,
          tax_number: formData.tax_number,
          tax_office: formData.tax_office,
          company_phone: formData.company_phone,
          company_address: formData.company_address,
        },
      });

      if (res.data?.success) {
        setPaytrToken(res.data.token);
        setPaytrOid(res.data.merchant_oid);
      } else {
        toast.error('PayTR odeme baslatilmadi');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PayTR ödeme başlatılamadı');
    }
    setIsSubmitting(false);
  };

  // Handle final step submission
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    if (isTrial) {
      // Trial: direct register (step 3 is package, which is final for trial)
      if (!validateStep2()) return;
      await handleRegisterDirect();
    } else if (selectedPaymentMethod?.type === 'credit_card' && selectedPaymentMethod?.has_payment_api) {
      // Credit card: pre-register + PayTR
      await handlePreRegisterPayTR();
    } else {
      // Bank transfer or no payment method: direct register
      await handleRegisterDirect();
    }
  };

  // Poll PayTR payment status - auto login on success
  const pollPaymentStatus = useCallback(async () => {
    if (!paytrOid) return;
    setCheckingPayment(true);
    try {
      const res = await axios.get(`${API_URL}/api/paytr/status/${paytrOid}`);
      if (res.data?.status === 'success') {
        toast.success('Ödemeniz başarıyla alınmıştır. Giriş yapılıyor...', { duration: 5000 });
        // Auto-login with stored credentials
        const loginRes = await login(formData.email, formData.password);
        if (loginRes?.success) {
          navigate('/dashboard');
        } else {
          navigate('/login');
        }
        return;
      } else if (res.data?.status === 'failed') {
        toast.error('Ödeme başarısız oldu. Lütfen tekrar deneyin.');
        setPaytrToken(null);
        setPaytrOid(null);
      }
    } catch {}
    setCheckingPayment(false);
  }, [paytrOid, navigate, formData.email, formData.password, login]);

  const copyIban = (iban) => {
    navigator.clipboard.writeText(iban).then(() => toast.success('IBAN kopyalandı'));
  };

  return (
    <div className="flex min-h-screen" data-testid="register-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[42%] relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-emerald-500/8 blur-[100px] translate-y-1/3" />

        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          <Link to="/" className="flex items-center gap-3 group" data-testid="register-logo-link">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-emerald-400 transition-transform group-hover:scale-105">
              <Building2 className="h-5 w-5" />
            </div>
            <span className="text-xl font-heading font-bold text-white">ÖdemeTakibi</span>
          </Link>

          <div className="space-y-8">
            <div>
              <Badge className="bg-emerald-400/20 text-emerald-400 border-0 mb-4">
                <Clock className="mr-1 h-3 w-3" />7 Gün Ücretsiz Deneme
              </Badge>
              <h1 className="text-3xl xl:text-4xl font-heading font-bold text-white leading-tight">
                Hemen başlayın,
                <br />
                <span className="text-emerald-400">sonuçları görün.</span>
              </h1>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-sm">
                Kredi kartı gerekmeden ücretsiz deneme. Dakikalar içinde tahsilat sürecinizi dijitalleştirin.
              </p>
            </div>

            <div className="space-y-3">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 text-emerald-400">
                    <f.icon className="h-4 w-4" />
                  </div>
                  {f.text}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-4 text-[11px] text-slate-500">
              <div className="flex items-center gap-1"><Shield className="h-3 w-3" />256-bit SSL</div>
              <div className="flex items-center gap-1"><CheckCircle className="h-3 w-3" />KVKK Uyumlu</div>
              <div className="flex items-center gap-1"><Lock className="h-3 w-3" />Güvenli Ödeme</div>            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex flex-1 flex-col bg-background">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[480px] px-6 py-8 sm:px-10 sm:py-10">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white">
                <Building2 className="h-5 w-5" />
              </div>
              <span className="text-xl font-heading font-bold">ÖdemeTakibi</span>
            </div>

            {/* Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-heading font-bold tracking-tight">Firma Kaydı</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {totalSteps} adımda hesabınızı oluşturun
              </p>
            </div>

            {/* Step Indicator */}
            <div className="mb-8">
              <div className="flex items-center gap-1" data-testid="step-indicator">
                {stepsForStepper.map((s, i) => {
                  const stepNum = i + 1;
                  const isActive = step === stepNum;
                  const isComplete = step > stepNum;
                  const StepIcon = s.icon;
                  return (
                    <React.Fragment key={i}>
                      <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-all text-xs font-medium ${isActive ? 'bg-primary/10 text-primary' : isComplete ? 'text-primary' : 'text-muted-foreground'}`}>
                        <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition-all ${isActive ? 'bg-primary text-white' : isComplete ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          {isComplete ? <CheckCircle className="h-3.5 w-3.5" /> : stepNum}
                        </div>
                        <span className="hidden sm:inline">{s.label}</span>
                      </div>
                      {i < stepsForStepper.length - 1 && (
                        <div className={`h-px flex-1 transition-all ${step > stepNum ? 'bg-primary' : 'bg-muted'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <form onSubmit={handleFinalSubmit}>
              {/* Step 1: Company Info */}
              {step === 1 && (
                <div className="space-y-5 animate-fade-in" data-testid="step-1">
                  <div>
                    <h3 className="text-base font-semibold">{t('companyInfo')}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Fatura bilgileriniz bu adımdan alınacaktır</p>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="company_name">Firma Adı *</Label>
                      <Input id="company_name" value={formData.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Firma Ticari Unvanı" className="h-10" data-testid="reg-company-name" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="tax_number">Vergi Numarası *</Label>
                        <Input id="tax_number" value={formData.tax_number} onChange={(e) => set('tax_number', e.target.value)} placeholder="VKN / TCKN" className="h-10" data-testid="reg-tax-number" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="tax_office">Vergi Dairesi *</Label>
                        <Input id="tax_office" value={formData.tax_office} onChange={(e) => set('tax_office', e.target.value)} placeholder="Vergi Dairesi" className="h-10" data-testid="reg-tax-office" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="company_phone">Firma Telefon *</Label>
                      <Input id="company_phone" value={formData.company_phone} onChange={(e) => set('company_phone', e.target.value)} placeholder="0XXX XXX XX XX" className="h-10" data-testid="reg-company-phone" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="company_address">Adres *</Label>
                      <Input id="company_address" value={formData.company_address} onChange={(e) => set('company_address', e.target.value)} placeholder="Firma adresi" className="h-10" data-testid="reg-company-address" />
                    </div>
                  </div>
                  <Button type="button" className="w-full h-11 font-semibold" onClick={() => { if (validateStep1()) setStep(2); }} data-testid="next-step-btn">
                    Devam Et<ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Step 2: Account Info (MOVED BEFORE PAYMENT) */}
              {step === 2 && (
                <div className="space-y-5 animate-fade-in" data-testid="step-2">
                  <div>
                    <h3 className="text-base font-semibold">Hesap Bilgileri</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Yönetici hesabınızı oluşturun</p>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="name">Ad Soyad *</Label>
                      <Input id="name" value={formData.name} onChange={(e) => set('name', e.target.value)} placeholder="Ad Soyad" className="h-10" data-testid="reg-name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="email">E-posta *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => set('email', e.target.value)} placeholder="email@firma.com" className="h-10" data-testid="reg-email" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="phone">Cep Telefonu *</Label>
                      <Input id="phone" value={formData.phone} onChange={(e) => set('phone', e.target.value)} placeholder="05XX XXX XX XX" className="h-10" data-testid="reg-phone" />
                    </div>

                    <Separator />

                    <div className="space-y-1.5">
                      <Label htmlFor="password">Şifre *</Label>
                      <div className="relative">
                        <Input id="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => set('password', e.target.value)} placeholder="En az 8 karakter, büyük/küçük harf, rakam" className="h-10 pr-10" data-testid="reg-password" />
                        <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="password_confirm">Şifre Tekrar *</Label>
                      <Input id="password_confirm" type={showPassword ? 'text' : 'password'} value={formData.password_confirm} onChange={(e) => set('password_confirm', e.target.value)} placeholder="Şifreyi tekrar girin" className="h-10" data-testid="reg-password-confirm" />
                    </div>

                    <Separator />

                    {/* Legal Consents */}
                    <div className="space-y-3">
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Sözleşme Onayları</p>
                      {[
                        { id: 'kvkk', key: 'kvkk', href: '/legal/kvkk', label: 'KVKK Aydınlatma Metni' },
                        { id: 'privacy', key: 'privacy', href: '/legal/gizlilik', label: 'Gizlilik Politikası' },
                        { id: 'membership', key: 'membership', href: '/legal/uyelik', label: 'Üyelik Sözleşmesi' },
                      ].map(c => (
                        <label key={c.id} htmlFor={c.id} className="flex items-start gap-2.5 cursor-pointer">
                          <Checkbox id={c.id} checked={consents[c.key]} onCheckedChange={(v) => setConsents(s => ({...s, [c.key]: !!v}))} data-testid={`consent-${c.key}`} />
                          <span className="text-xs leading-relaxed text-muted-foreground select-none">
                            <a href={c.href} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline" onClick={(e) => e.preventDefault() || window.open(c.href, '_blank')}>{c.label}</a>'nı okudum ve kabul ediyorum.
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setStep(1)} data-testid="prev-step-btn-2">
                      <ArrowLeft className="mr-2 h-4 w-4" />Geri
                    </Button>
                    <Button type="button" className="flex-1 h-11 font-semibold" onClick={() => { if (validateStep2()) setStep(3); }} data-testid="next-step-btn-2">
                      Devam Et<ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Package Selection */}
              {step === 3 && (
                <div className="space-y-5 animate-fade-in" data-testid="step-3">
                  <div>
                    <h3 className="text-base font-semibold">Paket Seçimi</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Tüm paketlerde 7 günlük ücretsiz deneme. Yıllık faturalandırılır.</p>
                  </div>
                  <div className="grid gap-3 grid-cols-2" data-testid="package-selection">
                    {packages.map((pkg) => {
                      const isSelected = formData.selected_package_slug === pkg.slug;
                      const isPopular = pkg.slug === 'profesyonel';
                      const yp = pkg.yearly_price || (pkg.monthly_price * 12);
                      return (
                        <button
                          key={pkg.id} type="button"
                          onClick={() => set('selected_package_slug', pkg.slug)}
                          className={`relative rounded-xl border-2 p-3.5 text-left transition-all duration-200 ${isSelected ? 'border-primary bg-primary/5 shadow-sm shadow-primary/10' : 'border-transparent bg-muted/40 hover:border-primary/30 hover:bg-muted/60'}`}
                          data-testid={`reg-package-${pkg.slug}`}
                        >
                          {isPopular && (
                            <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9px] px-2 py-0.5 bg-primary text-white border-0 shadow-sm">
                              Popüler
                            </Badge>
                          )}
                          {isSelected && <CheckCircle className="absolute top-2.5 right-2.5 h-4 w-4 text-primary" />}
                          <p className="font-semibold text-sm">{pkg.name}</p>
                          {pkg.monthly_price === 0 ? (
                            <p className={`font-bold text-lg mt-1.5 ${isSelected ? 'text-primary' : ''}`}>Ücretsiz</p>
                          ) : (
                            <>
                              <p className={`font-bold text-lg mt-1.5 ${isSelected ? 'text-primary' : ''}`}>
                                {yp.toLocaleString('tr-TR')} <span className="text-xs font-normal">TL/yil</span>
                              </p>
                              <p className="text-[10px] text-muted-foreground">Aylik {pkg.monthly_price.toLocaleString('tr-TR')} TL</p>
                            </>
                          )}
                          <Separator className="my-2" />
                          <div className="space-y-1">
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                              <Users className="h-3 w-3 shrink-0" />
                              {pkg.is_unlimited_personnel ? 'Sınırsız' : pkg.personnel_limit} Çalışan
                            </p>
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                              <MessageCircle className="h-3 w-3 shrink-0" />
                              {pkg.is_unlimited_messages ? 'Sınırsız' : pkg.monthly_message_limit} Mesaj/ay
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* For trial: this is the final step */}
                  {isTrial ? (
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setStep(2)} data-testid="prev-step-btn-3">
                        <ArrowLeft className="mr-2 h-4 w-4" />Geri
                      </Button>
                      <Button type="submit" className="flex-1 h-11 font-semibold" disabled={isSubmitting} data-testid="register-btn">
                        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                        Kayıt Ol (Ücretsiz Deneme)
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setStep(2)} data-testid="prev-step-btn-3">
                        <ArrowLeft className="mr-2 h-4 w-4" />Geri
                      </Button>
                      <Button type="button" className="flex-1 h-11 font-semibold" onClick={() => setStep(4)} data-testid="next-step-btn-3">
                        Devam Et<ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4 (for paid packages): Billing + Payment Method */}
              {step === 4 && !isTrial && (
                <div className="space-y-5 animate-fade-in" data-testid="step-4">
                  <div>
                    <h3 className="text-base font-semibold">Fatura & Ödeme</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Fatura bilgileriniz ve ödeme yöntemini seçin</p>
                  </div>

                  {/* Billing Info */}
                  <div className="rounded-xl border bg-muted/30 p-4 space-y-2">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Fatura Bilgileri</p>
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                      <span className="text-muted-foreground">Firma:</span>
                      <span className="font-medium">{formData.company_name}</span>
                      <span className="text-muted-foreground">Vergi No:</span>
                      <span className="font-medium font-mono">{formData.tax_number}</span>
                      <span className="text-muted-foreground">Vergi Dairesi:</span>
                      <span className="font-medium">{formData.tax_office}</span>
                      {formData.company_address && (
                        <>
                          <span className="text-muted-foreground">Adres:</span>
                          <span className="font-medium">{formData.company_address}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Paket:</span>
                      <span className="font-semibold">{selectedPkg?.name}</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm font-semibold">Yıllık Tutar:</span>
                      <span className="text-xl font-bold text-primary">{yearlyPrice.toLocaleString('tr-TR')} TL</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3 shrink-0" />7 günlük ücretsiz deneme sonrasında faturalandırılır
                    </p>
                  </div>

                  <Separator />

                  {/* Payment Method Selection */}
                  <div className="space-y-3">
                    <p className="text-sm font-semibold">Ödeme Yöntemi</p>
                    {paymentMethods.length === 0 ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 space-y-1">
                        <p className="flex items-center gap-1.5 font-medium"><Info className="h-4 w-4 shrink-0" />Henüz aktif ödeme yöntemi bulunmuyor</p>
                        <p className="text-xs text-amber-600">Kayıt olduktan sonra deneme süreniz başlayacaktır. Ödeme bilgileri daha sonra ayarlanacaktır.</p>
                      </div>
                    ) : (
                      <div className="grid gap-2" data-testid="payment-method-selection">
                        {paymentMethods.map(pm => (
                          <button
                            key={pm.id} type="button"
                            onClick={() => setSelectedPaymentMethod(pm)}
                            className={`rounded-xl border-2 p-4 text-left transition-all flex items-center gap-3 ${selectedPaymentMethod?.id === pm.id ? 'border-primary bg-primary/5 shadow-sm' : 'border-transparent bg-muted/30 hover:border-primary/30'}`}
                            data-testid={`select-pm-${pm.type}`}
                          >
                            <div className={`rounded-lg p-2.5 ${pm.type === 'credit_card' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                              {pm.type === 'credit_card' ? <CreditCard className="h-5 w-5" /> : <Building className="h-5 w-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">{pm.name}</p>
                              {pm.description && <p className="text-[11px] text-muted-foreground truncate">{pm.description}</p>}
                            </div>
                            {selectedPaymentMethod?.id === pm.id && <CheckCircle className="h-5 w-5 text-primary shrink-0" />}
                          </button>
                        ))}
                      </div>
                    )}

                    {selectedPaymentMethod?.type === 'bank_transfer' && selectedPaymentMethod?.iban && (
                      <div className="rounded-xl border border-green-200 bg-green-50 p-4 space-y-2" data-testid="bank-transfer-details">
                        <p className="text-[11px] font-semibold text-green-800 uppercase tracking-wider">Havale Bilgileri</p>
                        <div className="text-sm space-y-1.5">
                          {selectedPaymentMethod.bank_name && (
                            <p className="text-green-700">Banka: <span className="font-medium">{selectedPaymentMethod.bank_name}</span></p>
                          )}
                          <div className="flex items-center gap-2">
                            <p className="text-green-700">IBAN: <span className="font-mono font-medium">{selectedPaymentMethod.iban}</span></p>
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-green-600 hover:text-green-800" onClick={() => copyIban(selectedPaymentMethod.iban)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          {selectedPaymentMethod.account_holder && (
                            <p className="text-green-700">Hesap Sahibi: <span className="font-medium">{selectedPaymentMethod.account_holder}</span></p>
                          )}
                        </div>
                        <p className="text-[10px] text-green-600">Havale/EFT yaptıktan sonra ödemeniz onaylandığında aboneliğiniz aktif edilecektir.</p>
                      </div>
                    )}

                    {selectedPaymentMethod?.type === 'credit_card' && (
                      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800" data-testid="credit-card-details">
                        <p className="flex items-center gap-1.5 font-medium"><CreditCard className="h-4 w-4" />PayTR ile güvenli kredi kartı ödemesi</p>
                        <p className="text-[10px] text-blue-600 mt-1">
                          {selectedPaymentMethod.has_payment_api
                            ? 'Ödeme yap butonuna tıkladığınızda güvenli ödeme sayfası açılacaktır. Ödeme başarılı olursa hesabınız oluşturulacaktır.'
                            : 'PayTR yapılandırması henüz tamamlanmamıştır. Kayıt sonrası deneme süreniz başlayacaktır.'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setStep(3)} data-testid="prev-step-btn-4">
                      <ArrowLeft className="mr-2 h-4 w-4" />Geri
                    </Button>
                    <Button type="submit" className="flex-1 h-11 font-semibold" disabled={isSubmitting || (paymentMethods.length > 0 && !selectedPaymentMethod)} data-testid="register-btn">
                      {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : (
                        selectedPaymentMethod?.type === 'credit_card' && selectedPaymentMethod?.has_payment_api
                          ? <CreditCard className="mr-2 h-4 w-4" />
                          : <CheckCircle className="mr-2 h-4 w-4" />
                      )}
                      {selectedPaymentMethod?.type === 'credit_card' && selectedPaymentMethod?.has_payment_api
                        ? 'Ödeme Yap'
                        : 'Kayıt Ol'}
                    </Button>
                  </div>
                </div>
              )}
            </form>

            {/* Login link */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                Hesabınız var mı?{' '}
                <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="login-link">
                  Giriş Yap
                </Link>
              </p>
            </div>

            {/* PayTR iFrame Overlay */}
            {paytrToken && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="paytr-overlay">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 border-b bg-primary/5">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-sm">Güvenli Ödeme</h3>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">{yearlyPrice.toLocaleString('tr-TR')} TL</p>
                  </div>
                  <div className="p-1" style={{ minHeight: '400px' }}>
                    <iframe
                      src={`https://www.paytr.com/odeme/guvenli/${paytrToken}`}
                      id="paytriframe"
                      frameBorder="0"
                      scrolling="yes"
                      style={{ width: '100%', minHeight: '400px' }}
                      data-testid="paytr-iframe"
                    />
                  </div>
                  <div className="flex items-center justify-between px-5 py-3 border-t bg-muted/30">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setPaytrToken(null); setPaytrOid(null); toast.info('Ödeme iptal edildi. Hesabınız henüz oluşturulmadı.'); }}
                      data-testid="paytr-cancel-btn"
                    >
                      İptal Et
                    </Button>
                    <Button
                      size="sm"
                      onClick={pollPaymentStatus}
                      disabled={checkingPayment}
                      data-testid="paytr-check-btn"
                    >
                      {checkingPayment ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="mr-2 h-3.5 w-3.5" />}
                      Ödeme Durumunu Kontrol Et
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
